import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { useTranslation } from "react-i18next";
import DetailsPageLayout, {
  DetailsPageConfig,
  TableItem,
  UserData,
} from "../../../components/common/DetailsPageLayout";
import { executeWithdraw } from "../../../setup/api/services/withdraws";
import { IWithdrawalItem } from "../../../setup/api/services/withdraws/models";
import { getUserDetailsById } from "../../../setup/api/services/user";
import { IUserAllInfoResponseData } from "../../../setup/api/services/user/models";
import { MultiCashoutStatus } from "../constants";
import {
  createMultiCashout as executeMultiCashout,
  getMultiCashoutDetails,
} from "../../../setup/api/services/multiCashouts";
import {
  IMultiCashoutContractItem,
  IMultiCashoutDetailsResponseData,
} from "../../../setup/api/services/multiCashouts/models";
import { ContractsTableProps } from "../../../components/common/DetailsPageLayout/DetailsPageLayout";

const PREVIEW_SIZE = 3;
const SELLER_PAGE_SIZE = 10;
const BUYER_PAGE_SIZE = 10;

const MultiCashoutDetails = () => {
  const { id } = useParams();
  const participantId = id;

  const navigate = useNavigate();
  const { t } = useTranslation();

  const singleClick = useRef<boolean | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  // Seller
  const [sellerPage, setSellerPage] = useState(1);
  const [sellerPagesCount, setSellerPagesCount] = useState(1);
  const [previewSellerItems, setPreviewSellerItems] = useState<
    IMultiCashoutContractItem[]
  >([]);

  // Buyer
  const [buyerPage, setBuyerPage] = useState(1);
  const [buyerPagesCount, setBuyerPagesCount] = useState(1);
  const [previewBuyerItems, setPreviewBuyerItems] = useState<
    IMultiCashoutContractItem[]
  >([]);

  const [request, setRequest] = useState<IMultiCashoutDetailsResponseData>();
  const [userData, setUserData] = useState<UserData>();

  const fetchMultiCashoutDetails = useCallback(async () => {
    if (id) {
      try {
        setPageLoading(true);
        setLoading(true);
        const result = await getMultiCashoutDetails({
          cashOutParticipantId: id,
          sellerPage_page: 0,
          sellerPage_size: PREVIEW_SIZE,
          buyerPage_page: 0,
          buyerPage_size: PREVIEW_SIZE,
        });
        if (participantId) {
          const userResult = await getUserDetailsById(participantId);
          const userDataResponse: IUserAllInfoResponseData =
            userResult?.data || {};
          setUserData({
            profile: {
              fullName: userDataResponse.profile?.fullName,
              photoUrl: userDataResponse.profile?.photoUrl,
              addresses: userDataResponse.profile?.addresses,
            },
            phoneNumber: userDataResponse.phoneNumber,
            nationalIdVerified: userDataResponse.nationalIdVerified,
          });
        }
        setRequest(result.data);
        setPreviewSellerItems(
          result.data.sellerAndBrokerContracts?.content?.slice(0, 3) ?? []
        );
        setPreviewBuyerItems(
          result.data.buyerContracts?.content?.slice(0, 3) ?? []
        );
      } catch (error) {
        console.error("Error fetching multi cashout details:", error);
        setRequest(undefined);
      } finally {
        setPageLoading(false);
        setLoading(false);
      }
    }
  }, [id, participantId]);

  const fetchSellerAndBrokerContracts = useCallback(
    async (page: number) => {
      setSellerPage(page);
      if (id) {
        try {
          setLoading(true);
          const result = await getMultiCashoutDetails({
            cashOutParticipantId: id,
            sellerPage_page: page - 1,
            sellerPage_size: SELLER_PAGE_SIZE,
            userRoles: "PROVIDER",
          });
          setRequest((prev) =>
            prev
              ? {
                  ...prev,
                  sellerAndBrokerContracts:
                    result.data.sellerAndBrokerContracts,
                }
              : result.data
          );
          setSellerPagesCount(
            result.data.sellerAndBrokerContracts?.totalPages ?? 1
          );
        } catch (error) {
          console.error("Error fetching seller and broker contracts:", error);
        } finally {
          setLoading(false);
        }
      }
    },
    [id]
  );

  const fetchBuyerContracts = useCallback(
    async (page: number) => {
      setBuyerPage(page);
      if (id) {
        try {
          setLoading(true);
          const result = await getMultiCashoutDetails({
            cashOutParticipantId: id,
            buyerPage_page: page - 1,
            buyerPage_size: BUYER_PAGE_SIZE,
            userRoles: "CUSTOMER",
          });
          setRequest((prev) =>
            prev
              ? {
                  ...prev,
                  buyerContracts: result.data.buyerContracts,
                }
              : result.data
          );
          setBuyerPagesCount(result.data.buyerContracts?.totalPages ?? 1);
        } catch (error) {
          console.error("Error fetching buyer contracts:", error);
        } finally {
          setLoading(false);
        }
      }
    },
    [id]
  );

  const handleSellerPaginationChange = useCallback(
    (page: number) => {
      fetchSellerAndBrokerContracts(page);
    },
    [fetchSellerAndBrokerContracts]
  );

  const handleBuyerPaginationChange = useCallback(
    (page: number) => {
      fetchBuyerContracts(page);
    },
    [fetchBuyerContracts]
  );

  const handleTransferClick = useCallback(async () => {
    if (!id || !!singleClick?.current) return;
    let isSuccess = false;
    setLoading(true);
    singleClick.current = true;
    try {
      const result = await executeMultiCashout({
        beneficiaryId: Number(id),
        amount: request?.totalSellerAmount?.toString(),
        beneficiaryName: userData?.profile?.fullName || "",
      });
      isSuccess = !!result?.successful;
      console.log("result:", result);
      if (!isSuccess) {
        console.error("Withdraw execution failed:", result?.error?.message);
      }
      if (isSuccess) {
        setTimeout(
          () => navigate(`${ROUTES.WITHDRAWS}/${result?.data?.withdrawalId}`),
          3000
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error("Error executing withdraw:", errorMessage);
    } finally {
      setLoading(false);
      if (!isSuccess) {
        setTimeout(() => window.location.reload(), 3000);
      }
    }
  }, [id, navigate, request?.totalSellerAmount, userData?.profile?.fullName]);

  useEffect(() => {
    fetchMultiCashoutDetails();
  }, [fetchMultiCashoutDetails]);

  const handleBackButtonClick = () => {
    const searchParams = new URLSearchParams(location?.search);
    if (searchParams?.get("isOperation") === "true") {
      navigate(ROUTES.COMPLETED_TRANSFERS);
      return;
    }
    navigate(ROUTES.MULTI_CASHOUTS);
  };

  const handleContractClick = useCallback(
    (contractId: string) => {
      navigate(`${ROUTES.CONTRACT}${contractId}`);
    },
    [navigate]
  );

  // Map IWithdrawalItem to TableItem
  const mapToTableItems = useCallback(
    (items: IWithdrawalItem[]): TableItem[] => {
      return items.map((item) => ({
        id: item.contractId || "",
        title: item.title,
        contractId: item.contractId,
        amount: item.amount,
      }));
    },
    []
  );

  const getItemStatus = useCallback(
    (item: TableItem, overallStatus?: string) => {
      //TODO: add status logic
      // return overallStatus === MultiCashoutStatus?.COMPLETED
      //   ? overallStatus
      //   : MultiCashoutStatus?.IN_PROGRESS;
      return MultiCashoutStatus?.INITIATED;
    },
    []
  );
  const allStatuses = useMemo(() => {
    //TODO: add status logic
    return MultiCashoutStatus?.INITIATED;
  }, []);

  const sellerAndBrokerContracts: ContractsTableProps = {
    transactionsSectionTitle: "معاملات في انتظار التحويل",
    items: mapToTableItems(
      request?.sellerAndBrokerContracts?.content?.map((item) => ({
        id: item.shortId,
        title: item.title,
        contractId: item.shortId,
        amount: item.amount,
      })) ?? []
    ),
    previewItems: mapToTableItems(
      previewSellerItems.map((item) => ({
        id: item.shortId,
        title: item.title,
        contractId: item.shortId,
        amount: item.amount,
      })) ?? []
    ),
    tableHeaders: {
      transaction: "المعاملة",
      transactionNumber: "رقم المعاملة",
      totalValue: "القيمة الإجمالية",
      status: "الحالة",
    },
    showAllLinkText: t("lbl_show_child_contracts"),
    onContractClick: handleContractClick,
    getItemStatus: getItemStatus,
    totalPages: sellerPagesCount,
    onPageChange: handleSellerPaginationChange,
    currentPage: sellerPage,
  };

  const buyerContracts: ContractsTableProps | undefined = {
    transactionsSectionTitle: "معاملات في انتظار الاسترداد",
    items: mapToTableItems(
      request?.buyerContracts?.content?.map((item) => ({
        id: item.shortId,
        title: item.title,
        contractId: item.shortId,
        amount: item.amount,
      })) ?? []
    ),
    previewItems: mapToTableItems(
      previewBuyerItems.map((item) => ({
        id: item.shortId,
        title: item.title,
        contractId: item.shortId,
        amount: item.amount,
      })) ?? []
    ),
    tableHeaders: {
      transaction: "المعاملة",
      transactionNumber: "رقم المعاملة",
      totalValue: "القيمة الإجمالية",
      status: "الحالة",
    },
    showAllLinkText: t("lbl_show_child_contracts"),
    onContractClick: handleContractClick,
    getItemStatus: getItemStatus,
    totalPages: buyerPagesCount,
    onPageChange: handleBuyerPaginationChange,
    currentPage: buyerPage,
  };

  const config: DetailsPageConfig = {
    transactionContracts: sellerAndBrokerContracts,
    numberOfTransactions: request?.totalSellerContractsCount,
    refundContracts:
      (request?.buyerContracts?.totalElements ?? 0) > 0
        ? buyerContracts
        : undefined,
    transactionsCountLabel: "عدد المعاملات المرتبطة بالسحب",
    actionButtonText: t("lbl_release"),
    addressErrorText: "لا يوجد عنوان لهذا المستخدم. يجب إضافة عنوان أولاً",
    userData: userData,
    totalAmount: request?.totalSellerAmount,
    // referenceNumber: request?.cashOutReferenceNumber || undefined,
    status: allStatuses,

    showActionButton: allStatuses === MultiCashoutStatus?.INITIATED,
    // request?.status === WITHDRAW_STATUS?.IN_PROGRESS ||
    // request?.status === WITHDRAW_STATUS?.FAILED,

    onBackClick: handleBackButtonClick,
    onActionButtonClick: handleTransferClick,
    actionButtonDisabled:
      loading || !!singleClick?.current || !userData?.profile?.addresses?.[0],
    showReferenceNumber: true,
    // !!request?.cashOutReferenceNumber,
    showAddressError: !userData?.profile?.addresses?.[0],
    // && request?.status === WITHDRAW_STATUS?.IN_PROGRESS,
    pageLoading: pageLoading,
    actionLoading: loading,
  };

  return <DetailsPageLayout {...config} />;
};

export default MultiCashoutDetails;
